import { makeAutoObservable, reaction, runInAction } from "mobx";
import {
  DocumentLanguage,
  GetProjectDocumentRecipientsResponse,
  isOk,
  LocalizedText,
  Result,
  SigningMethod,
  TranslationOption,
} from "@/common/common";
import {
  BooleanFieldModel,
  documentLanguageFieldModel,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { DocumentType } from "@/common/documents";
import { Language } from "@/i18n/languages";
import {
  CleanupFunction,
  cleanupFunctions,
  DbotEvent,
  DbotEventDispatcher,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  DynamicRecipient,
  DynamicRecipientType,
  isDocumentSent,
  isDocumentSentForSigning,
  isDocumentSigned,
  isIncorporationDocument,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  ProjectCompany,
  ProjectDocumentStatus,
  Recipient,
  RecipientId,
  ResendDocumentForViewing,
  selectBatchCompaniesEvent,
  SendDocumentForViewingModel,
  shareholdersWithShares,
  SharesForShareholder,
  Subscriber,
  updateCompanyRoleEmailsEvent,
  UpdateCompanyRoleEmailsResultEvent,
} from "@/dbot/common/commonDomain";
import {
  compact,
  countBy,
  first,
  identity,
  mapValues,
  partial,
  reject,
  remove,
  sumBy,
} from "lodash";
import { TFunction, TMessage } from "@/i18n/tt";
import { when } from "@/utils/functions";
import { move } from "@/utils/arrays";
import { newUuid } from "@/utils/uuid";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import {
  downloadSignedDocumentAction,
  findProjectBoardMeetingFolder,
  openSendForSigningDialogAction,
  RecipientFieldModel,
  RecipientsEditModel,
} from "@/dbot/summary/summaryDomain";
import {
  resolveUrnReference,
  toCompanyRoleReference,
  toShareholderReference,
  toSubscriberIdReference,
} from "@/utils/UrnReference";
import { getFullName, isBoardMember, sortedRoles } from "@/utils/Roles";
import { ShareholderDTO } from "@/types/Shareholder";
import { CompanyRole } from "@/types/CompanyInfo";
import { AlertProps } from "@/components-v2/Alert";
import { TransactionType } from "@/types/enums/TransactionType";
import { Transaction } from "@/types/Transactions";
import { isAfter, isBefore } from "@/utils/Dates";
import { DropdownMenuItems, DropdownMenuProps } from "@/components-v2/Dropdown";
import { DecisionType } from "@/common/decisions";
import { isExtensionPdf } from "@/utils/files";
import { SelectFilesButtonProps } from "@/components-v2/SelectFilesButton";
import { TMessageKey } from "@/i18n/types.gen";
import { showGeneralError, thunk } from "@/events/eventsDomain";
import { autotextRegex, DataRequestType, dataRequestTypeLabels } from "@/ai/autotext";
import { recordsEqual } from "@/utils/collections";
import { orderlessFilter } from "@/utils/filter.ts";

export type CustomDocumentInsideAddressOption = "recipient" | "freetext";

export type CustomDocumentDateOption = "sendoutDate" | "custom";

export type CustomDocumentType =
  | "letter"
  | "agreement"
  | "incorporation"
  | "mergerPlan"
  | "mergerShareholderNotice"
  | "optionGrantLetter"
  | "blob"
  | "boardStatementContributionInKind"
  | "boardStatementDebtConversion"
  | "setOffDeclaration"
  | "boardStatementCreditorNotice";

const lockedCustomDocumentDecisionTypes: Partial<Record<CustomDocumentType, DecisionType>> = {
  incorporation: DecisionType.incorporation,
  mergerPlan: DecisionType.merger,
  mergerShareholderNotice: DecisionType.merger,
};

export const isLockedCustomDocumentType = (type?: CustomDocumentType): boolean =>
  type !== undefined && lockedCustomDocumentDecisionTypes[type] !== undefined;

const mergerCustomDocumentTypes: CustomDocumentType[] = ["mergerPlan", "mergerShareholderNotice"];

const isMergerCustomDocumentType = (type?: CustomDocumentType): boolean =>
  type !== undefined && mergerCustomDocumentTypes.includes(type);

export type CustomDocumentInitialRecipients = "boardAndCeo";

export type CustomDocumentData = {
  type: DocumentType.custom_document;
  blocks?: CustomDocumentBlock[];
  customDocumentDate?: string;
  documentDateOption?: CustomDocumentDateOption;
  documentLanguage?: Language[];
  insideAddressEnabled?: boolean;
  insideAddressFreetext?: string;
  insideAddressOption?: CustomDocumentInsideAddressOption;
  headerLogoEnabled?: boolean;
  headerCompanyNameEnabled?: boolean;
  headerDateEnabled?: boolean;
  headerTitlesEnabled?: boolean;
  headerEnabled?: boolean;
  recipients?: Recipient[];
  dynamicRecipients?: DynamicRecipient[];
  signatureEnabled?: boolean;
  signatureSendersStatement?: LocalizedText[];
  signatureSenders?: CustomDocumentSignatory[];
  signatoriesStatement?: LocalizedText[];
  signatories?: CustomDocumentSignatory[];
  title?: LocalizedText[];
  translationOption?: TranslationOption;
  customDocumentType?: CustomDocumentType;
  customDocumentSendOption: CustomDocumentSendOption;
  requireSignature: boolean;
  lockedSigningMethod?: SigningMethod;
  headerChangeMode: CustomDocumentChangeMode;
  signaturesChangeMode: CustomDocumentChangeMode;
  filenames: Record<string, string>;
};

export type CustomDocumentSendOption = "personalised" | "commonAllRecipients";
export type CustomDocumentChangeMode = "automatic" | "custom";

const changeModeLabels: { [key in CustomDocumentChangeMode]: TMessageKey } = {
  automatic: "common.auto",
  custom: "dbot.custom-document.custom",
};

function createChangeMode(mode: CustomDocumentChangeMode) {
  return new RadioFieldModel<CustomDocumentChangeMode>({
    value: mode ?? "automatic",
    labelFn: (i) => ({ key: changeModeLabels[i] }),
    options: ["automatic", "custom"],
    idFn: identity,
  });
}
export type CustomDocumentOverrides = {
  type: DocumentType.custom_document;
  recipients?: Recipient[];
};

export type CustomDocumentBlock =
  | CustomDocumentFreetextBlock
  | CustomDocumentShareholdersBlock
  | CustomDocumentSubscribersBlock
  | CustomDocumentTransactionLogBlock
  | CustomDocumentPersonalDrawdownContributionBlock;

export type CustomDocumentBlockNumbering = "section" | "parenthesised";

export type CustomDocumentFreetextBlock = {
  type: "freetext";
  id: string;
  heading: LocalizedText[];
  text: LocalizedText[];
  pageBreakBefore?: boolean;
  numbering?: CustomDocumentBlockNumbering;
  refName?: string;
  sourceKey?: string;
};

export type CustomDocumentShareholdersBlock = {
  type: "shareholders";
  id: string;
  perDate?: string;
};

export type CustomDocumentSubscribersBlock = {
  type: "subscribers";
  id: string;
};

export type CustomDocumentTransactionLogBlock = {
  type: "transactionLog";
  id: string;
  heading: LocalizedText[];
  from: string;
  to: string;
  transactionTypes: TransactionType[];
  isPersonalised: boolean;
};

export type CustomDocumentPersonalDrawdownContributionBlock = {
  type: "personalDrawdownContribution";
  id: string;
  heading: LocalizedText[];
};

export const customBlockLabels: { [key in CustomDocumentBlock["type"]]: TMessage } = {
  freetext: { key: "dbot.custom-document.blocks.freetext.label" },
  shareholders: { key: "dbot.custom-document.blocks.shareholders.label" },
  subscribers: { key: "dbot.custom-document.blocks.subscribers.label" },
  transactionLog: { key: "dbot.custom-document.blocks.transactions.label" },
  personalDrawdownContribution: {
    key: "dbot.custom-document.blocks.personal-drawdown-contribution.label",
  },
};

export type CustomDocumentSignatoryOption =
  | "company"
  | "ceo"
  | "chairperson"
  | "freetext"
  | "recipient";

export type CustomDocumentSignatory = {
  id: string;
  option: CustomDocumentSignatoryOption;
  freetextName?: string;
  freetextRole?: LocalizedText[];
};

export type CustomDocumentBlockForm =
  | CustomDocumentFreetextBlockForm
  | CustomDocumentShareholdersBlockForm
  | CustomDocumentSubscribersBlockForm
  | CustomDocumentTransactionLogBlockForm
  | CustomDocumentPersonalDrawdownContributionBlockForm;

export type CustomDocumentFreetextBlockForm = {
  type: "freetext";
  id: string;
  heading: LocalizedTextEditorModel;
  text: LocalizedTextEditorModel;
  storedHeading: LocalizedText[];
  storedText: LocalizedText[];
  pageBreakBefore: boolean;
  numbering?: CustomDocumentBlockNumbering;
  refName?: string;
  sourceKey?: string;
};

export type CustomDocumentShareholdersBlockForm = {
  type: "shareholders";
  id: string;
  currentOrPerDate: RadioFieldModel<"current" | "per-date">;
  perDate: LocalDateFieldModel;
  showDateInput: boolean;
};

export type CustomDocumentSubscribersBlockForm = {
  type: "subscribers";
  id: string;
};

export type CustomDocumentTransactionLogBlockForm = {
  type: "transactionLog";
  id: string;
  heading: LocalizedTextEditorModel;
  from: LocalDateFieldModel;
  to: LocalDateFieldModel;
  transactionTypes: OptionFieldModel<TransactionType>;
  initTransactionTypes: TransactionType[];
  isPersonalised: BooleanFieldModel;
};

export type CustomDocumentPersonalDrawdownContributionBlockForm = {
  type: "personalDrawdownContribution";
  id: string;
  heading: LocalizedTextEditorModel;
};

export type CustomDocumentSignatoryForm = {
  id: string;
  option: CustomDocumentSignatoryOption;
  freetextName: StringFieldModel;
  freetextRole: LocalizedTextEditorModel;
};

export const customDocumentTypeLabels: Record<CustomDocumentType, TMessage> = {
  agreement: { key: "dbot.custom-document.type.agreement" },
  letter: { key: "dbot.custom-document.type.letter" },
  incorporation: { key: "decisionType.incorporation.name" },
  mergerPlan: { key: "dbot.custom-document.type.merger-plan" },
  mergerShareholderNotice: { key: "dbot.custom-document.type.merger-shareholder-notice" },
  optionGrantLetter: { key: "dbot.custom-document.type.option-grant-letter" },
  blob: { key: "common.pdf" },
  boardStatementContributionInKind: {
    key: "dbot.custom-document.type.board-statement-contribution-in-kind",
  },
  boardStatementDebtConversion: {
    key: "dbot.custom-document.type.board-statement-debt-conversion",
  },
  setOffDeclaration: {
    key: "dbot.custom-document.type.set-off-declaration",
  },
  boardStatementCreditorNotice: {
    key: "dbot.custom-document.type.board-statement-creditor-notice",
  },
};

export type RecipientsDialogState = {
  orgNo: string;
  item: QuickAddItem;
  search: StringFieldModel;
  selectedIds: RecipientId[];
};

export type QuickAddItem = {
  name: string;
  label: TMessage;
  count?: number;
  disabled: boolean;
  recipients: Recipient[];
  tooltip?: TMessage;
  isDynamic?: boolean;
};

export class CustomDocumentForm {
  readonly type = DocumentType.custom_document;
  documentLanguage: RadioFieldModel<DocumentLanguage>;
  title: LocalizedTextEditorModel;
  storedTitle: LocalizedText[];
  insideAddressEnabled: boolean;
  insideAddressOption: RadioFieldModel<CustomDocumentInsideAddressOption>;
  insideAddressFreetext: StringFieldModel;
  headerLogoEnabled: boolean;
  headerCompanyNameEnabled: boolean;
  headerTitlesEnabled: boolean;
  headerDateEnabled: boolean;
  headerEnabled: boolean;
  documentDateOption: RadioFieldModel<CustomDocumentDateOption>;
  customDocumentDate: LocalDateFieldModel;
  blocks: CustomDocumentBlockForm[];
  signatureEnabled: boolean;
  signatureSendersStatement: LocalizedTextEditorModel;
  signatureSenders: CustomDocumentSignatoryForm[];
  signatoriesStatement: LocalizedTextEditorModel;
  signatories: CustomDocumentSignatoryForm[];
  sendDocumentForViewingModel?: SendDocumentForViewingModel;
  transactionLog: Transaction[] = [];

  customDocumentTypeHint?: TMessage;
  selectedCompany?: string;
  recipientUpdateResult?: Result<TMessage, TMessage>;
  recipientsByCompany: {
    [orgNo: string]: RecipientsEditModel;
  } = {};

  requireSignature: boolean;
  lockedSigningMethod?: SigningMethod;
  personalised: boolean;
  headerChangeMode: RadioFieldModel<CustomDocumentChangeMode>;
  signaturesChangeMode: RadioFieldModel<CustomDocumentChangeMode>;
  customDocumentType?: CustomDocumentType;
  isBlob = false;
  filenames: Record<string, string>;

  blobStatus?: "deleting";
  recipientsDialog?: RecipientsDialogState;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DocumentContext<CustomDocumentData, CustomDocumentOverrides>,
    options?: { justAdded?: boolean },
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.customDocumentType = context.documentData.customDocumentType;
    this.headerChangeMode = createChangeMode(context.documentData.headerChangeMode);
    this.signaturesChangeMode = createChangeMode(context.documentData.signaturesChangeMode);
    this.requireSignature = context.documentData.requireSignature;
    this.lockedSigningMethod = context.documentData.lockedSigningMethod;
    this.personalised = context.documentData.customDocumentSendOption === "personalised";
    this.documentLanguage = documentLanguageFieldModel({
      orgNo: context.orgNo,
      value: mapLocalesToDocumentLanguage(context.documentData.documentLanguage),
      onChange: (lang) => this.onLanguageChange(mapDocumentLanguageToLocales(lang)),
    });
    this.title = new LocalizedTextEditorModel({
      texts: context.documentData.title,
      aiClient: context.aiClient,
      isReadOnly: () => context.isLocked || this.isPanelsLocked(),
      label: { key: "title" },
    });
    this.storedTitle = context.documentData.title ?? [];
    this.headerLogoEnabled = context.documentData.headerLogoEnabled ?? true;
    this.headerCompanyNameEnabled = context.documentData.headerCompanyNameEnabled ?? true;
    this.headerTitlesEnabled = context.documentData.headerTitlesEnabled ?? true;
    this.headerDateEnabled = context.documentData.headerDateEnabled ?? true;
    this.headerEnabled = context.documentData.headerEnabled ?? true;
    this.insideAddressEnabled = context.documentData.insideAddressEnabled ?? true;
    this.insideAddressOption = new RadioFieldModel({
      options: ["recipient", "freetext"],
      value: context.documentData.insideAddressOption ?? "recipient",
      idFn: identity,
      labelFn: (x) => {
        switch (x) {
          case "recipient":
            return { key: "dbot.custom-document.recipient-auto" };
          default:
            return { key: "dbot.freetext" };
        }
      },
    });
    this.insideAddressFreetext = new StringFieldModel({
      value: context.documentData.insideAddressFreetext,
    });
    this.documentDateOption = new RadioFieldModel({
      options: ["sendoutDate", "custom"],
      value: context.documentData.documentDateOption ?? "sendoutDate",
      idFn: identity,
      labelFn: (x) => {
        switch (x) {
          case "sendoutDate":
            return { key: "dbot.custom-document.sendout-date-auto" };
          case "custom":
            return { key: "dbot.custom-document.custom-date" };
        }
      },
    });
    this.customDocumentDate = new LocalDateFieldModel({
      value: context.documentData.customDocumentDate,
    });
    this.blocks =
      context.documentData.blocks?.map(partial(initBlockForm, context, this.transactionLog)) ?? [];
    this.signatureEnabled = context.documentData.signatureEnabled ?? true;
    this.signatureSendersStatement = new LocalizedTextEditorModel({
      texts: context.documentData.signatureSendersStatement,
      aiClient: context.aiClient,
      isReadOnly: () =>
        context.isLocked ||
        this.isPanelsLocked() ||
        this.signaturesChangeMode.value === "automatic",
      label: { key: "dbot.custom-document.senders-statement" },
    });
    this.signatureSenders = initSignatureSenders(context);
    this.signatoriesStatement = new LocalizedTextEditorModel({
      texts: context.documentData.signatoriesStatement,
      aiClient: context.aiClient,
      isReadOnly: () =>
        context.isLocked ||
        this.isPanelsLocked() ||
        this.signaturesChangeMode.value === "automatic",
      label: { key: "dbot.custom-document.signatories-statement" },
    });
    this.signatories = initSignatories(context);

    this.customDocumentTypeHint = when(options?.justAdded, () => {
      switch (context.documentData.customDocumentType) {
        case "letter":
          return { key: "dbot.custom-document.type.letter.info" };
        case "agreement":
          return { key: "dbot.custom-document.type.agreement.info" };
      }
    });

    this.selectedCompany = context.orgNo;
    for (const c of context.companies) {
      this.initRecipientsEditModel(c.orgNo);
    }

    this.onLanguageChange(context.documentData.documentLanguage);
    this.fetchTransactionLog();
    this.isBlob = this.customDocumentType === "blob";
    this.filenames = context.documentData.filenames;
  }

  init(): CleanupFunction {
    const initSignatures = () => {
      runInAction(() => {
        this.signatureSenders = initSignatureSenders(this.context);
        this.signatories = initSignatories(this.context);
      });
    };

    return cleanupFunctions([
      reaction(
        () => this.context.documentData.signaturesChangeMode,
        (newVal, oldVal) => {
          if (newVal === "automatic" && newVal !== oldVal) {
            initSignatures.call(this);
          }
        },
      ),
      reaction(
        () => this.context.documentData.requireSignature,
        (newVal, oldVal) => {
          if (newVal !== oldVal && this.context.documentData.signaturesChangeMode === "automatic") {
            initSignatures.call(this);
          }
        },
      ),
      reaction(
        () => this.context.documentData.headerChangeMode,
        (newVal, oldVal) => {
          if (newVal === "automatic" && newVal !== oldVal) {
            runInAction(
              () =>
                (this.insideAddressEnabled =
                  this.context.documentData.insideAddressEnabled ?? true),
            );
          }
        },
      ),
      reaction(
        () => this.context.documentData.filenames,
        (newVal, oldVal) => {
          if (!recordsEqual(oldVal, newVal)) {
            runInAction(() => (this.filenames = this.context.documentData.filenames));
          }
        },
      ),
    ]);
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/remove-custom-panel":
        switch (event.id) {
          case "signature":
            this.set("signatureEnabled", false);
            break;
          case "header":
            this.set("headerEnabled", false);
            break;
          default:
            remove(this.blocks, (b) => b.id === event.id);
            break;
        }
        break;
      case "dbot/update-company-role-emails-result":
        this.onCompanyRoleUpdated(event);
        break;
      case "dbot/resend-notice-to-updated-recipient-result":
        if (event.documentId === this.context.documentId && isOk(event.result)) {
          this.onResendToRecipientResult(event.data);
        }
        break;
    }
  }

  toggleRequireSignature() {
    this.requireSignature = !this.requireSignature;
  }

  togglePersonalised() {
    this.personalised = !this.personalised;
  }

  recipientCount(orgNo: string) {
    const model = this.recipientsByCompany[orgNo];
    return model.recipientFields.length + this.countDynamicRecipients(orgNo);
  }

  countDynamicRecipients(orgNo: string) {
    return this.getDynamicRecipients(orgNo).reduce((count, d) => {
      if (d.type === "debtConverters") {
        return count + this.context.subscribers.filter((s) => s.isDebtConverter).length;
      }
      return count;
    }, 0);
  }

  getDynamicRecipients(orgNo: string = this.context.orgNo): DynamicRecipient[] {
    return this.getRecipientsEditModel(orgNo)?.toDynamicRecipients() ?? [];
  }

  getRecipients(orgNo: string = this.context.orgNo): Recipient[] {
    return this.getRecipientsEditModel(orgNo)?.toRecipients() ?? [];
  }

  visibleRecipients = (dialog: RecipientsDialogState): Recipient[] =>
    orderlessFilter(dialog.item.recipients, (r) => [r.name, r.email], dialog.search.inputValue);

  getRecipientsForPreview(orgNo: string = this.context.orgNo): Recipient[] {
    const model = this.getRecipientsEditModel(orgNo);
    const inline = model?.toRecipients() ?? [];
    const dynamic =
      model?.dynamicRecipients?.flatMap((x) => {
        switch (x.type) {
          case "debtConverters":
            return this.filterDebtConverters(orgNo);
          default:
            return [];
        }
      }) ?? [];
    return [...inline, ...dynamic];
  }

  getRecipientsEditModel(orgNo: string): RecipientsEditModel | undefined {
    return this.recipientsByCompany[orgNo];
  }

  initRecipientsEditModel(orgNo: string) {
    if (this.recipientsByCompany[orgNo]) {
      return;
    }
    const recipients =
      orgNo === this.context.orgNo
        ? (this.context.documentData.recipients ?? [])
        : (this.context.documentOverrides[orgNo]?.recipients ?? []);
    this.recipientsByCompany[orgNo] = new RecipientsEditModel(
      recipients,
      this.context.documentData.dynamicRecipients,
      partial(this.onRecipientChange, orgNo),
    );
  }

  clearCustomDocumentTypeHint() {
    this.customDocumentTypeHint = undefined;
  }

  fetchTransactionLog() {
    if (this.blocks.some((b) => b.type === "transactionLog") && !this.transactionLog.length) {
      this.dispatch({
        type: "fetchTransactionLog",
        orgNo: this.context.orgNo,
        onSuccess: (transactions) => {
          this.transactionLog = transactions;
          this.blocks.forEach(partial(updateBlockTransactionLog, transactions));
        },
      });
    }
  }

  onLanguageChange(documentLangs?: Language[]) {
    this.storedTitle = [
      ...this.title.localizedTexts,
      ...this.storedTitle.filter((s) =>
        this.title.localizedTexts.every((t) => t.language !== s.language),
      ),
    ];
    this.title.onLanguageChange(documentLangs, this.storedTitle);
    this.signatureSendersStatement.onLanguageChange(
      documentLangs,
      this.context.documentData.signatureSendersStatement,
    );
    this.signatureSenders.forEach(
      partial(updateSignatoryLanguage, this.context.documentData.signatureSenders, documentLangs),
    );
    this.signatoriesStatement.onLanguageChange(
      documentLangs,
      this.context.documentData.signatoriesStatement,
    );
    this.signatories.forEach(
      partial(updateSignatoryLanguage, this.context.documentData.signatories, documentLangs),
    );
    this.blocks.forEach(partial(updateBlockLanguage, this.context, documentLangs));
  }

  onRecipientChange(orgNo: string, type: "add" | "remove" | "change", m: RecipientFieldModel) {
    this.recipientUpdateResult = undefined;
    const ref = resolveUrnReference(m.ref);
    if (type === "change") {
      switch (ref?.type) {
        case "companyroleId":
          this.dispatch(
            updateCompanyRoleEmailsEvent(orgNo, [
              { companyroleId: ref.id, email: m.emailField.validatedValue },
            ]),
          );
          break;
        case "shareholderId":
          this.dispatch({
            type: "dbot/update-shareholder-email",
            shareholderId: ref.id,
            email: m.emailField.validatedValue,
            onFulfilled: (result) => this.onShareholderRecipientUpdated(m, result),
          });
          break;
      }
    }
  }

  onCompanyRoleUpdated(event: UpdateCompanyRoleEmailsResultEvent) {
    this.recipientUpdateResult = event.result;
    if (isOk(event.result)) {
      for (const item of event.items) {
        this.recipientsByCompany[event.orgNo]?.recipientFields
          .find((r) => r.id === toCompanyRoleReference(item.companyroleId))
          ?.rebase();
      }
    }
  }

  onShareholderRecipientUpdated(
    recipient: RecipientFieldModel,
    result: Result<TMessage, TMessage>,
  ) {
    this.recipientUpdateResult = result;
    if (isOk(result)) {
      recipient.rebase();
    }
  }

  onResendToRecipientResult(data: ResendDocumentForViewing) {
    const companyRoleRef = toCompanyRoleReference(data.companyroleId ?? 0);
    const shareholderRef = toShareholderReference(data.shareholderId ?? 0);
    Object.values(this.recipientsByCompany).forEach((x) => {
      const field = x.recipientFields.find(
        (f) => f.ref === companyRoleRef || f.ref === shareholderRef,
      );
      field?.emailField.setAndCommitInputValue(data.recipientEmail);
    });
  }

  addBlock(type: CustomDocumentBlock["type"]) {
    const blockData = initNewBlockData(this.context, type);
    this.blocks.push(initBlockForm(this.context, this.transactionLog, blockData));
    this.fetchTransactionLog();
    this.dispatch([
      {
        type: "dbot/open-details-panel",
        panel: { type: "custom", id: blockData.id, documentId: this.context.documentId },
      },
    ]);
  }

  moveBlock(panel: DetailsPanel, toPanel: DetailsPanel) {
    const block = this.blocks.find((b) => b.id === panel.id);
    const toIndex = this.blocks.findIndex((b) => b.id === toPanel.id);
    if (block) {
      this.blocks = move(this.blocks, block, toIndex);
    }
  }

  setInsideAddressEnabled(enabled: boolean) {
    this.insideAddressEnabled = enabled;
  }

  addNewRecipient(orgNo: string) {
    const recipientsEditModel = this.getRecipientsEditModel(orgNo);
    recipientsEditModel?.addRecipient({
      id: newUuid(),
      name: "",
      email: "",
    });
    if (recipientsEditModel) {
      recipientsEditModel.validationError = undefined;
    }
  }

  quickAddRecipients(orgNo: string, item: QuickAddItem) {
    if (!item.isDynamic && item.recipients.length > 1) {
      this.openRecipientsDialog(orgNo, item);
    } else {
      this.addRecipients(orgNo, item.recipients, item.name, item.isDynamic);
    }
  }

  openRecipientsDialog(orgNo: string, item: QuickAddItem) {
    this.recipientsDialog = {
      orgNo,
      item,
      search: new StringFieldModel(),
      selectedIds: item.recipients.map((r) => r.id),
    };
  }

  closeRecipientsDialog() {
    this.recipientsDialog = undefined;
  }

  toggleRecipient(id: RecipientId) {
    const dialog = this.recipientsDialog;
    if (!dialog) return;
    dialog.selectedIds = dialog.selectedIds.includes(id)
      ? dialog.selectedIds.filter((it) => it !== id)
      : [...dialog.selectedIds, id];
  }

  selectAllRecipients() {
    const dialog = this.recipientsDialog;
    if (!dialog) return;
    const visibleIds = this.visibleRecipients(dialog).map((r) => r.id);
    dialog.selectedIds = [...new Set([...dialog.selectedIds, ...visibleIds])];
  }

  deselectAllRecipients() {
    const dialog = this.recipientsDialog;
    if (!dialog) return;
    const visibleIds = this.visibleRecipients(dialog).map((r) => r.id);
    dialog.selectedIds = dialog.selectedIds.filter((id) => !visibleIds.includes(id));
  }

  addSelectedRecipients() {
    const dialog = this.recipientsDialog;
    if (!dialog) return;
    const selected = dialog.item.recipients.filter((r) => dialog.selectedIds.includes(r.id));
    this.addRecipients(dialog.orgNo, selected, dialog.item.name);
    this.closeRecipientsDialog();
  }

  addRecipients(orgNo: string, recipients: Recipient[], name?: string, isDynamic?: boolean) {
    if (isDynamic) {
      const r: DynamicRecipient = {
        orgNo,
        type: name as DynamicRecipientType,
      };
      this.getRecipientsEditModel(orgNo)?.addDynamicRecipient(r);
    } else {
      for (const r of recipients) {
        this.initRecipientsEditModel(orgNo);
        this.getRecipientsEditModel(orgNo)?.addRecipient(r);
      }
    }
    this.validate();
  }

  get recipientsAlert(): AlertProps | undefined {
    switch (this.recipientUpdateResult?.state) {
      case "ok":
        return {
          type: "success",
          message: this.recipientUpdateResult.data,
          onClose: this.dismissRecipientsAlert,
        };
      case "failure":
        return {
          type: "error",
          message: this.recipientUpdateResult.error,
          onClose: this.dismissRecipientsAlert,
        };
    }
  }

  dismissRecipientsAlert() {
    this.recipientUpdateResult = undefined;
  }

  closeSendCustomDocumentDialog() {
    this.sendDocumentForViewingModel = undefined;
  }

  createRecipientModel(res: GetProjectDocumentRecipientsResponse): {
    [orgNo: string]: RecipientsEditModel;
  } {
    return Object.fromEntries(
      res.companies.map((c) => [
        c.organisationNumber,
        new RecipientsEditModel(
          res.recipients,
          [],
          partial(this.onRecipientChange, c.organisationNumber),
        ),
      ]),
    );
  }

  openSendCustomDocumentDialog(recipientsByCompany?: { [orgNo: string]: RecipientsEditModel }) {
    this.dismissRecipientsAlert();
    const successAction: DbotEvent = {
      type: "dbot/fetch-document-recipients",
      documentId: this.context.documentId,
      onSuccess: (res) => [
        thunk(() => {
          this.sendDocumentForViewingModel = new SendDocumentForViewingModel({
            recipientsByCompany: recipientsByCompany ?? this.createRecipientModel(res),
            onClose: () => this.closeSendCustomDocumentDialog(),
            language: this.context.locale,
            aiClient: this.context.aiClient,
            dispatch: this.dispatch,
            documentId: this.context.documentId,
            isDocumentIndividualised: this.personalised,
            documents: res.documents,
            companies: res.companies,
            title: getCustomDocumentTitle(this, this.context.locale),
            recipientUpdateResult: this.recipientUpdateResult,
          });
        }),
      ],
      onFailure: [showGeneralError()],
    };
    this.dispatch([
      {
        type: "dbot/validate-project-action",
        contextDocumentId: this.context.documentId,
        label: { key: "send" },
        successAction,
      },
    ]);
  }

  get showSettingsRightLabel() {
    return !this.isBlob;
  }

  get hasSignaturePanel() {
    return !this.isBlob;
  }

  get canAddBlocks() {
    if (isMergerCustomDocumentType(this.customDocumentType)) {
      return true;
    }
    return !this.isBlob && !isLockedCustomDocumentType(this.customDocumentType);
  }

  canSendDocument() {
    return (
      this.customDocumentType === "mergerShareholderNotice" ||
      !isLockedCustomDocumentType(this.customDocumentType)
    );
  }

  isPanelsLocked() {
    return (
      isLockedCustomDocumentType(this.customDocumentType) &&
      !isMergerCustomDocumentType(this.customDocumentType)
    );
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.context.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.context.documentId);
  }

  get isSentForSigning() {
    return (
      isDocumentSentForSigning(this.context, this.context.documentId) ||
      isDocumentSigned(this.context, this.context.documentId)
    );
  }

  get isSentForViewing() {
    return isDocumentSent(this.context, this.context.documentId);
  }

  // The sealing date of the stiftelsesdokument must not be after the other incorporation
  // documents, so they cannot be sent for signing until it is sealed.
  get awaitsIncorporationDocumentSealing() {
    if (this.documentStatus?.incorporationDocumentPart === undefined) {
      return false;
    }
    const incorporationDocument = this.context.currentDocuments.find(isIncorporationDocument);
    return (
      incorporationDocument !== undefined &&
      !isDocumentSigned(this.context, incorporationDocument.documentId)
    );
  }

  get awaitingIncorporationDocumentSealingInfo(): TMessage | undefined {
    if (!this.awaitsIncorporationDocumentSealing) {
      return undefined;
    }
    switch (this.documentStatus?.incorporationDocumentPart) {
      case "CONFIRMATION_OF_PAID_SHARE_CAPITAL_CONTRIBUTION":
        return {
          key: "dbot.custom-document.awaiting-incorporation-document-signature.paid-share-capital",
        };
      case "CONFIRMATION_OF_AUDITOR_ROLE":
        return {
          key: "dbot.custom-document.awaiting-incorporation-document-signature.auditor",
        };
      case "CONFIRMATION_OF_ACCOUNTANT_ROLE":
        return {
          key: "dbot.custom-document.awaiting-incorporation-document-signature.accountant",
        };
      default:
        return {
          key: "dbot.custom-document.awaiting-incorporation-document-signature.business-information",
        };
    }
  }

  get showsBusinessInformationInfo() {
    return (
      this.documentStatus?.incorporationDocumentPart === "BUSINESS_INFORMATION" &&
      !this.isSentForSigning
    );
  }

  get openSendForSigningDialogAction() {
    return openSendForSigningDialogAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      showUnsupportedAttachmentWarning:
        this.context.attachments.length > 0 &&
        this.context.attachments.some((a) => !isExtensionPdf(a.filename)),
      lockedSigningMethod:
        this.context.documentData.lockedSigningMethod ??
        (this.customDocumentType === "incorporation" ? SigningMethod.BANKID : undefined),
      preselectDestinationFolder:
        this.customDocumentType === "boardStatementContributionInKind" ||
        this.customDocumentType === "boardStatementDebtConversion"
          ? (entries) => findProjectBoardMeetingFolder(entries, this.context.projectId)
          : undefined,
      attachments: this.context.attachments,
    });
  }

  get downloadSignedDocumentAction() {
    const numberOfSignedDocuments =
      this.documentItem.documentData?.type === DocumentType.custom_document &&
      (this.documentItem.documentData.customDocumentSendOption === "personalised" ||
        this.documentItem.documentData.customDocumentType === "blob")
        ? this.documentStatus?.numberOfSignedDocuments
        : undefined;
    return downloadSignedDocumentAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      numberOfSignedDocuments,
    });
  }

  get hasRecipients() {
    return !this.recipientsByCompany[this.context.orgNo].isEmpty;
  }

  get newRecipients(): { [orgNo: string]: RecipientFieldModel[] } {
    const sentToIds =
      this.context.documentStatuses.find((s) => s.documentId === this.context.documentId)
        ?.viewerRecipientIds ?? [];

    return mapValues(this.recipientsByCompany, (m) =>
      m.recipientFields.filter((r) => !sentToIds.includes(String(r.id))),
    );
  }

  get newRecipientsCount(): number {
    return sumBy(Object.values(this.newRecipients), (r) => r.length);
  }

  get hasNewRecipients() {
    return Object.values(this.newRecipients).some((xs) => xs.length > 0);
  }

  openSettingsPanel() {
    this.dispatch([
      {
        type: "dbot/open-details-panel",
        panel: { type: "custom", id: "document-settings", documentId: this.context.documentId },
      },
    ]);
  }

  openRecipientsPanel() {
    this.dispatch([openCustomDocumentRecipientsPanelEvent(this.context.documentId)]);
  }

  get isSigningInProgress() {
    return (
      isDocumentSentForSigning(this.context, this.context.documentId) &&
      !isDocumentSigned(this.context, this.context.documentId)
    );
  }

  openSendToNewRecipientsDialog() {
    if (this.personalised && this.isSigningInProgress) {
      this.openSendToNewSignersDialog();
      return;
    }
    const newRecipientsByCompany = mapValues(this.newRecipients, (xs, orgNo) => {
      const m = new RecipientsEditModel([], [], partial(this.onRecipientChange, orgNo));
      m.validate();
      m.recipientFields = xs;
      return m;
    });
    this.openSendCustomDocumentDialog(newRecipientsByCompany);
  }

  openSendToNewSignersDialog() {
    const newRecipientIds = new Set(
      Object.values(this.newRecipients).flatMap((xs) => xs.map((r) => String(r.id))),
    );
    const showUnsupportedAttachmentWarning =
      this.context.attachments.length > 0 &&
      this.context.attachments.some((a) => !isExtensionPdf(a.filename));
    const lockedSigningMethod =
      this.context.documentData.lockedSigningMethod ??
      (this.customDocumentType === "incorporation" ? SigningMethod.BANKID : undefined);
    this.dispatch([
      {
        type: "dbot/fetch-document-signers",
        documentId: this.context.documentId,
        onSuccess: (data) => {
          const newSigners = data.signers.filter((s) => {
            const ref = s.reference;
            if (!ref) return false;
            const m = /^urn:recipientId:(.*)$/.exec(ref);
            return m ? newRecipientIds.has(m[1]) : false;
          });
          return [
            {
              type: "dbot/fetch-destination-folders",
              onSuccess: (res) => [
                {
                  type: "dbot/open-review-dialog",
                  document: this.documentItem,
                  signers: newSigners,
                  documents: data.documents,
                  companies: data.companies,
                  label: { key: "send-for-signing" },
                  showUnsupportedAttachmentWarning,
                  lockedSigningMethod,
                  destinationFolders: res.entries,
                },
              ],
              onFailure: [showGeneralError()],
            },
          ];
        },
        onFailure: [showGeneralError()],
      },
    ]);
  }

  isValidForCompany(orgNo: string): boolean {
    return this.recipientsByCompany[orgNo]?.isValid ?? true;
  }

  get isValid(): boolean {
    return Object.values(this.recipientsByCompany).every((x) => x.isValid);
  }

  validate(): boolean {
    Object.values(this.recipientsByCompany).forEach((x) => x.validate());
    return this.isValid;
  }

  set<F extends keyof this>(field: F, value: this[F]) {
    this[field] = value;
  }

  addSignatory(list: "signatureSenders" | "signatories", option: CustomDocumentSignatoryOption) {
    this[list].push(
      initSignatory(this.context, {
        id: newUuid(),
        option,
        freetextRole: this.context.documentLanguages.map((language) => ({ language, text: "" })),
      }),
    );
  }

  moveSignatory(
    list: "signatureSenders" | "signatories",
    item: CustomDocumentSignatoryForm,
    toItem: CustomDocumentSignatoryForm,
  ) {
    this[list] = move(this[list], item, this[list].indexOf(toItem));
  }

  removeSignatory(list: "signatureSenders" | "signatories", item: CustomDocumentSignatoryForm) {
    this[list] = reject(this[list], item);
  }

  toggleCompany(orgNo: string, open: boolean = true) {
    if (!open) {
      this.selectedCompany = undefined;
    } else {
      this.selectedCompany = orgNo;
      this.initRecipientsEditModel(orgNo);
      this.dispatch([{ type: "dbot/select-company", orgNo }]);
    }
  }

  openCompany(orgNo: string) {
    this.dispatch({
      type: "dbot/open-document-blob",
      orgNo,
    });
  }

  deleteDocumentBlob(orgNo: string) {
    this.blobStatus = "deleting";
    this.dispatch({
      type: "dbot/delete-document-blob",
      projectDocumentId: this.documentItem.documentId,
      orgNo,
      onComplete: [
        thunk(() => {
          this.blobStatus = undefined;
        }),
      ],
    });
  }

  uploadDocumentBlob(files: File[], orgNo: string) {
    this.dispatch({
      type: "dbot/upload-document-blob",
      projectDocumentId: this.documentItem.documentId,
      orgNo,
      files,
    });
  }

  uploadingBlobState(orgNo: string) {
    return this.context.getUploadingDocumentBlob(this.documentItem.documentId, orgNo);
  }

  filterDebtConverters(orgNo: string) {
    const subscribers = this.context.orgNo === orgNo ? this.context.subscribers : undefined;
    return (
      subscribers
        ?.filter((s) => s.isDebtConverter)
        ?.map((s) => ({ ...subscriberToRecipient(s), id: s.subscriberId })) ?? []
    );
  }
}

export const signatoryOptionLabel: Record<CustomDocumentSignatoryOption, TMessage> = {
  company: { key: "company" },
  ceo: {
    key: "dbot.custom-document.x-auto",
    params: { x: getRoleTypeLabel(RoleType.CEO) },
  },
  chairperson: {
    key: "dbot.custom-document.x-auto",
    params: { x: getRoleTypeLabel(RoleType.CHAIRPERSON) },
  },
  freetext: { key: "dbot.freetext" },
  recipient: {
    key: "dbot.custom-document.x-auto",
    params: { x: { key: "dbot.recipient" } },
  },
};

const signatureSenderOptions: CustomDocumentSignatoryOption[] = [
  "company",
  "ceo",
  "chairperson",
  "freetext",
];

const signatureSignatoryOptions: CustomDocumentSignatoryOption[] = [
  "recipient",
  "ceo",
  "chairperson",
  "freetext",
];

const initSignatory = (
  context: DocumentContext<CustomDocumentData>,
  s: CustomDocumentSignatory,
): CustomDocumentSignatoryForm => ({
  id: s.id,
  option: s.option,
  freetextName: new StringFieldModel({ value: s.freetextName }),
  freetextRole: new LocalizedTextEditorModel({
    texts: s.freetextRole,
    aiClient: context.aiClient,
    isReadOnly: () => context.isLocked,
    label: { key: "signatures" }, // not used
  }),
});

const newBlockNumbering = (data: CustomDocumentData): CustomDocumentBlockNumbering | undefined =>
  data.blocks?.some((b) => b.type === "freetext" && b.numbering === "section")
    ? "section"
    : undefined;

const initNewBlockData = (
  context: DocumentContext<CustomDocumentData>,
  type: CustomDocumentBlock["type"],
): CustomDocumentBlock => {
  switch (type) {
    case "freetext":
      return {
        type: "freetext",
        id: newUuid(),
        heading:
          context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
        text: context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
        pageBreakBefore: false,
        numbering: newBlockNumbering(context.documentData),
      };
    case "shareholders":
      return {
        type: "shareholders",
        id: newUuid(),
      };
    case "subscribers":
      return {
        type: "subscribers",
        id: newUuid(),
      };
    case "transactionLog":
      return {
        type: "transactionLog",
        id: newUuid(),
        heading:
          context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
        from: new Date().getFullYear() - 1 + "-01-01",
        to: new Date().getFullYear() - 1 + "-12-31",
        transactionTypes: Object.values(TransactionType),
        isPersonalised: false,
      };
    case "personalDrawdownContribution":
      return {
        type: "personalDrawdownContribution",
        id: newUuid(),
        heading:
          context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
      };
  }
};

const initSignatureSenders = (
  context: DocumentContext<CustomDocumentData>,
): CustomDocumentSignatoryForm[] =>
  context.documentData.signatureSenders?.map((x) => initSignatory(context, x)) ?? [];

const initSignatories = (
  context: DocumentContext<CustomDocumentData>,
): CustomDocumentSignatoryForm[] =>
  context.documentData.signatories?.map((x) => initSignatory(context, x)) ?? [];

const initBlockForm = (
  context: DocumentContext<CustomDocumentData>,
  transactionsLog: Transaction[],
  block: CustomDocumentBlock,
): CustomDocumentBlockForm => {
  const includeShareCapitalIncrease = context.jsonData.decisions.some(
    (d) => d.decisionData.type === DecisionType.increaseOfShareCapital,
  );
  const includeReductionOfShareCapital = context.jsonData.decisions.some(
    (d) => d.decisionData.type === DecisionType.reductionOfShareCapital,
  );
  const includeIncorporation = context.jsonData.decisions.some(
    (d) => d.decisionData.type === DecisionType.incorporation,
  );
  const includeMerger = context.jsonData.decisions.some(
    (d) => d.decisionData.type === DecisionType.merger,
  );
  switch (block.type) {
    case "freetext":
      return {
        type: "freetext",
        id: block.id,
        heading: new LocalizedTextEditorModel({
          texts: block.heading,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.heading" },
        }),
        text: new LocalizedTextEditorModel({
          texts: block.text,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.text" },
          dataProvider: {
            includeShareCapitalIncrease: includeShareCapitalIncrease,
            includeCommittedCapital: context.featureToggles.committedCapital ?? false,
            includeReductionOfShareCapital: includeReductionOfShareCapital,
            includeIncorporation: includeIncorporation,
            includeMerger: includeMerger,
            includeOptions: context.featureToggles.financialInstrumentOptions ?? false,
          },
        }),
        storedHeading: block.heading,
        storedText: block.text,
        pageBreakBefore: block.pageBreakBefore ?? false,
        numbering: block.numbering,
        refName: block.refName,
        sourceKey: block.sourceKey,
      };
    case "shareholders": {
      const perDate = new LocalDateFieldModel({ value: block.perDate });
      const currentOrPerDate = makeCurrentOrPerDateRadioModel(perDate);
      return {
        type: "shareholders",
        id: block.id,
        currentOrPerDate,
        perDate,
        get showDateInput() {
          return currentOrPerDate.value === "per-date";
        },
      };
    }
    case "subscribers": {
      return {
        type: "subscribers",
        id: block.id,
      };
    }
    case "transactionLog": {
      const from = new LocalDateFieldModel({
        value: block.from,
        maxDate: block.to,
        required: true,
      });
      const to = new LocalDateFieldModel({ value: block.to, minDate: block.from, required: true });
      return {
        type: "transactionLog",
        id: block.id,
        heading: new LocalizedTextEditorModel({
          texts: block.heading,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.heading" },
        }),
        from,
        to,
        transactionTypes: new OptionFieldModel({
          value: block.transactionTypes,
          options: () => [
            ...new Set(
              transactionsLog
                .filter(
                  (a) =>
                    isBefore(block.from, a.transactionDate) && isAfter(block.to, a.transactionDate),
                )
                .map((d) => d.transactionType),
            ),
          ],
          idFn: (x) => x,
          labelFn: (value) => ({ key: value }),
        }),
        initTransactionTypes: block.transactionTypes,
        isPersonalised: new BooleanFieldModel({ value: block.isPersonalised }),
      };
    }
    case "personalDrawdownContribution": {
      return {
        type: "personalDrawdownContribution",
        id: block.id,
        heading: new LocalizedTextEditorModel({
          texts: block.heading,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.heading" },
        }),
      };
    }
  }
};

const makeCurrentOrPerDateRadioModel = (
  perDate: LocalDateFieldModel,
): RadioFieldModel<"current" | "per-date"> =>
  new RadioFieldModel<"current" | "per-date">({
    options: ["current", "per-date"],
    value: perDate.isEmpty ? "current" : "per-date",
    idFn: identity,
    labelFn: (x) => {
      switch (x) {
        case "current":
          return { key: "current" };
        case "per-date":
          return { key: "dbot.per-date" };
      }
    },
    onChange: () => {
      perDate.setAndCommitInputValue("");
    },
  });

const freetextByIdPred =
  (id: string) =>
  (x: CustomDocumentBlock): x is CustomDocumentFreetextBlock =>
    x.type === "freetext" && x.id === id;

const withStoredTranslations = (
  model: LocalizedTextEditorModel,
  stored: LocalizedText[],
): LocalizedText[] => [
  ...model.localizedTexts,
  ...stored.filter((s) => model.localizedTexts.every((t) => t.language !== s.language)),
];

const updateBlockLanguage = (
  context: DocumentContext<CustomDocumentData>,
  documentLangs: Language[] | undefined,
  form: CustomDocumentBlockForm,
) => {
  const data = context.documentData.blocks?.find(freetextByIdPred(form.id));
  switch (form.type) {
    case "freetext":
      form.storedHeading = withStoredTranslations(form.heading, form.storedHeading);
      form.storedText = withStoredTranslations(form.text, form.storedText);
      form.heading.onLanguageChange(documentLangs, form.storedHeading);
      form.text.onLanguageChange(documentLangs, form.storedText);
      break;
    case "transactionLog":
      form.heading.onLanguageChange(documentLangs, data?.heading ?? []);
      break;
    case "personalDrawdownContribution":
      form.heading.onLanguageChange(documentLangs, data?.heading ?? []);
      break;
  }
};

const updateSignatoryLanguage = (
  sourceList: CustomDocumentSignatory[] | undefined,
  documentLangs: Language[] | undefined,
  form: CustomDocumentSignatoryForm,
) => {
  form.freetextRole.onLanguageChange(
    documentLangs,
    sourceList?.find((x) => x.id === form.id)?.freetextRole,
  );
};

const updateBlockTransactionLog = (transactions: Transaction[], form: CustomDocumentBlockForm) => {
  switch (form.type) {
    case "transactionLog":
      form.transactionTypes.updateOptions(() => [
        ...new Set(
          transactions
            .filter(
              (a) =>
                isBefore(form.from.toISOString() ?? "", a.transactionDate) &&
                isAfter(form.to.toISOString() ?? "", a.transactionDate),
            )
            .map((d) => d.transactionType),
        ),
      ]);
      form.transactionTypes.setSelected(form.initTransactionTypes);
      break;
  }
};

const applySignatoryForm = (form: CustomDocumentSignatoryForm): CustomDocumentSignatory => ({
  id: form.id,
  option: form.option,
  freetextName: form.option === "freetext" ? form.freetextName.validatedValue : undefined,
  freetextRole: form.option === "freetext" ? form.freetextRole.localizedTexts : undefined,
});

const blockTextWithStoredTranslations = (
  block: CustomDocumentFreetextBlockForm,
  model: LocalizedTextEditorModel,
  stored: LocalizedText[],
): LocalizedText[] =>
  block.sourceKey === undefined ? model.localizedTexts : withStoredTranslations(model, stored);

const titleWithStoredTranslations = (form: CustomDocumentForm): LocalizedText[] => {
  const title = form.title.localizedTexts;
  if (!isMergerCustomDocumentType(form.customDocumentType)) {
    return title;
  }
  return [
    ...title,
    ...form.storedTitle.filter((s) => title.every((t) => t.language !== s.language)),
  ];
};

export const applyForm = (form: CustomDocumentForm): CustomDocumentData => ({
  type: DocumentType.custom_document,
  documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
  title: titleWithStoredTranslations(form),
  insideAddressEnabled: form.insideAddressEnabled,
  insideAddressOption: form.insideAddressOption.value,
  insideAddressFreetext: form.insideAddressFreetext.validatedValue,
  headerEnabled: form.headerEnabled,
  headerLogoEnabled: form.headerLogoEnabled,
  headerCompanyNameEnabled: form.headerCompanyNameEnabled,
  headerTitlesEnabled: form.headerTitlesEnabled,
  headerDateEnabled: form.headerDateEnabled,
  documentDateOption: form.documentDateOption.value,
  customDocumentDate: form.customDocumentDate.validatedValue,
  recipients: form.getRecipients(),
  dynamicRecipients: form.getDynamicRecipients(),
  blocks: form.blocks.map((b) => {
    switch (b.type) {
      case "freetext":
        return {
          type: "freetext",
          id: b.id,
          heading: blockTextWithStoredTranslations(b, b.heading, b.storedHeading),
          text: blockTextWithStoredTranslations(b, b.text, b.storedText),
          pageBreakBefore: b.pageBreakBefore,
          numbering: b.numbering,
          refName: b.refName,
          sourceKey: b.sourceKey,
        };
      case "shareholders":
        return {
          type: "shareholders",
          id: b.id,
          perDate: b.perDate.validatedValue,
        };
      case "subscribers":
        return {
          type: "subscribers",
          id: b.id,
        };
      case "transactionLog":
        return {
          type: "transactionLog",
          id: b.id,
          heading: b.heading.localizedTexts,
          from: b.from.validatedValue ?? "",
          to: b.to.validatedValue ?? "",
          transactionTypes: b.transactionTypes?.selected ?? [],
          isPersonalised: b.isPersonalised.value ?? false,
        };
      case "personalDrawdownContribution":
        return {
          type: "personalDrawdownContribution",
          id: b.id,
          heading: b.heading.localizedTexts,
        };
    }
  }),
  signatureEnabled: form.signatureEnabled,
  signatureSendersStatement: form.signatureSendersStatement.localizedTexts,
  signatureSenders: form.signatureSenders.map(applySignatoryForm),
  signatoriesStatement: form.signatoriesStatement.localizedTexts,
  signatories: form.signatories.map(applySignatoryForm),
  customDocumentSendOption: form.personalised ? "personalised" : "commonAllRecipients",
  requireSignature: form.requireSignature,
  lockedSigningMethod: form.lockedSigningMethod,
  headerChangeMode: form.headerChangeMode.value,
  signaturesChangeMode: form.signaturesChangeMode.value,
  customDocumentType: form.customDocumentType,
  filenames: form.filenames,
});

export const applyOverrides = (
  form: CustomDocumentForm,
  orgNo: string,
): CustomDocumentOverrides | undefined => {
  return {
    type: DocumentType.custom_document,
    recipients: form.recipientsByCompany[orgNo]?.toRecipients(),
  };
};

export const getCustomDocumentTitle = (
  form: CustomDocumentForm,
  lang: Language,
): TMessage | undefined => {
  const text =
    form.title.localizedTexts.find((l) => l.language === lang)?.text ??
    first(form.title.localizedTexts)?.text;
  if (text) {
    return { translated: text };
  }
};

export const getCustomDocumentSettingsPanels = (form: CustomDocumentForm): DetailsPanel[] => [
  {
    type: "custom",
    id: "document-settings",
    documentId: form.context.documentId,
    icon: "gear",
    heading: { key: "dbot.setup" },
    label: form.showSettingsRightLabel
      ? form.documentLanguage.getLabel(form.documentLanguage.value)
      : undefined,
  },
  {
    type: "custom",
    id: "recipients",
    hasValidationError: !form.isValid,
    documentId: form.context.documentId,
    icon: "users",
    heading: { key: "dbot.recipients" },
    label: form.recipientsByCompany.isEmpty
      ? { key: "none" }
      : {
          translated: String(form.recipientCount(form.context.orgNo)),
        },
  },
];

export function getDisplayLabelForBlobPanel(
  companies: ProjectCompany[],
  filenames?: Record<string, string>,
): TMessage {
  const total = companies.length;
  const setCount = filenames ? Object.keys(filenames).length : 0;

  if (setCount === 0) return { key: "not-set" };
  if (total === 1) {
    const name = filenames?.[companies[0].orgNo];
    return name ? { translated: name } : { key: "not-set" };
  }
  if (setCount === total) return { key: "completed" };

  const diff = total - setCount;
  return diff === 1
    ? { key: "dbot.custom-document.company-not-set" }
    : { key: "dbot.custom-document.x-companies-not-set", params: { x: diff } };
}

export const getCustomDocumentHeaderPanels = (form: CustomDocumentForm): DetailsPanel[] => [
  form.isBlob
    ? {
        type: "custom",
        id: "blob-header",
        documentId: form.context.documentId,
        icon: "file-lock",
        heading: { key: "common.pdf" },
        canRemove: false,
        label: getDisplayLabelForBlobPanel(form.context.companies, form.filenames),
      }
    : {
        type: "custom",
        id: "header",
        documentId: form.context.documentId,
        icon: "article",
        heading: { key: "dbot.custom-document.header" },
        canRemove: !form.context.isLocked && !form.isPanelsLocked(),
      },
];

export const getCustomDocumentSignaturePanels = (form: CustomDocumentForm): DetailsPanel[] =>
  compact([
    form.hasSignaturePanel && {
      type: "custom",
      id: "signature",
      documentId: form.context.documentId,
      icon: "signature",
      heading: { key: "signatures" },
      canRemove: !form.context.isLocked && !form.isPanelsLocked(),
    },
  ]);

const globalAutotextRegexp = new RegExp(autotextRegex, "g");
const singleAutotextRegExp = new RegExp(`^${autotextRegex.source}$`);

export const getSummaryHeading = (text = ""): TMessage | undefined => {
  return (
    when(singleAutotextRegExp.exec(text), (match) => ({
      key: dataRequestTypeLabels[match[1] as DataRequestType],
    })) ?? {
      translated: text.replaceAll(globalAutotextRegexp, "[$1]"),
    }
  );
};

function getLabelForPanel(b: CustomDocumentFreetextBlockForm): TMessage {
  if (b.text.localizedTexts.every((t) => globalAutotextRegexp.test(t.text))) {
    return { key: "common.auto" };
  }
  return { key: "dbot.freetext" };
}

export const getCustomDocumentBlockPanels = (form: CustomDocumentForm): DetailsPanel[] =>
  form.blocks.map<DetailsPanel>((b) => {
    switch (b.type) {
      case "freetext":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "text-lines",
          heading: customBlockLabels.freetext,
          summaryHeading: getSummaryHeading(
            b.heading.localizedTexts[0]?.text || b.text.localizedTexts[0]?.text,
          ),
          label: getLabelForPanel(b),
          canRemove: !form.context.isLocked && b.sourceKey === undefined,
        };
      case "shareholders":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "table",
          heading: customBlockLabels.shareholders,
          label: { key: "dbot.table" },
          canRemove: !form.context.isLocked,
        };
      case "subscribers":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "table",
          heading: customBlockLabels.subscribers,
          label: { key: "dbot.table" },
          canRemove: !form.context.isLocked,
        };
      case "transactionLog":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "table",
          heading: customBlockLabels.transactionLog,
          label: { key: "dbot.table" },
          canRemove: !form.context.isLocked,
        };
      case "personalDrawdownContribution":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "code-block",
          heading: customBlockLabels.personalDrawdownContribution,
          label: { key: "dbot.drawdown" },
          canRemove: !form.context.isLocked,
        };
    }
  });

export const getCustomDocumentPanels = (form: CustomDocumentForm): DetailsPanel[] => [
  ...getCustomDocumentSettingsPanels(form),
  ...getCustomDocumentHeaderPanels(form),
  ...getCustomDocumentBlockPanels(form),
  ...getCustomDocumentSignaturePanels(form),
];

export const autofocusHeading = (block: CustomDocumentFreetextBlockForm): boolean =>
  !block.heading.isEmpty;

export const autofocusText = (block: CustomDocumentFreetextBlockForm): boolean =>
  block.heading.isEmpty;

export const showAutoTranslateOptions = (form: CustomDocumentForm): boolean =>
  mapDocumentLanguageToLocales(form.documentLanguage.value).length > 1;

const shareholderToRecipient = (s: ShareholderDTO): Recipient => ({
  id: newUuid(),
  name: s.name,
  email: s.email,
  att: s.isCompany ? getFullName(s.contactInfo) : undefined,
  ref: toShareholderReference(s.id),
});

const companyRoleToRecipient = (r: CompanyRole): Recipient => ({
  id: newUuid(),
  name: getFullName(r),
  email: r.email ?? "",
  ref: toCompanyRoleReference(r.companyroleId),
});

const subscriberToRecipient = (s: Subscriber): Recipient => ({
  id: newUuid(),
  name: s.name,
  email: s.email,
  ref: toSubscriberIdReference(s.subscriberId),
});

export const prepAddBlockDropdown = (
  form: CustomDocumentForm,
  uploadAttachmentsButton: SelectFilesButtonProps | undefined,
): DropdownMenuProps | undefined =>
  form.context.isLocked || !form.canAddBlocks
    ? undefined
    : {
        button: {
          type: "button",
          variant: "secondary",
          label: { key: "dbot.add-section" },
          icon: "plus",
        },
        items: [
          { type: "label", label: { key: "dbot.custom-document.content" } },
          !form.headerEnabled && {
            type: "item",
            label: { key: "dbot.custom-document.header" },
            icon: "article",
            onSelect: () => form.set("headerEnabled", true),
          },
          {
            type: "item",
            label: customBlockLabels.freetext,
            icon: "text-lines",
            onSelect: () => form.addBlock("freetext"),
          },
          {
            type: "item",
            label: customBlockLabels.shareholders,
            icon: "table",
            onSelect: () => form.addBlock("shareholders"),
          },
          {
            type: "item",
            label: customBlockLabels.transactionLog,
            icon: "table",
            onSelect: () => form.addBlock("transactionLog"),
          },
          form.context.subscribers.length > 0 && {
            type: "item",
            label: customBlockLabels.subscribers,
            icon: "table",
            onSelect: () => form.addBlock("subscribers"),
          },
          form.context.subscribers.length > 0 && {
            type: "item",
            label: customBlockLabels.personalDrawdownContribution,
            icon: "code-block",
            onSelect: () => form.addBlock("personalDrawdownContribution"),
          },
          !form.signatureEnabled && {
            type: "item",
            label: { key: "signatures" },
            icon: "signature",
            onSelect: () => form.set("signatureEnabled", true),
          },
          { type: "label", label: { key: "dbot.custom-document.more-options" } },
          uploadAttachmentsButton && {
            type: "item",
            label: { key: "upload-attachments" },
            icon: "upload",
            onSelect: () => {
              if (uploadAttachmentsButton.inputId) {
                document.getElementById(uploadAttachmentsButton.inputId)?.click();
              }
            },
          },
          {
            type: "item",
            label: { key: "dbot.companies" },
            icon: "buildings",
            onSelect: () => form.dispatch(selectBatchCompaniesEvent(form.context)),
          },
        ],
      };

export const prepAddSenderItems = (form: CustomDocumentForm): DropdownMenuItems =>
  signatureSenderOptions
    .filter((x) => {
      switch (x) {
        case "freetext":
          return true;
        default:
          return !form.signatureSenders.some((y) => y.option === x);
      }
    })
    .map((x) => ({
      type: "item",
      id: x,
      label: signatoryOptionLabel[x],
      onSelect: () => form.addSignatory("signatureSenders", x),
    }));

export const prepAddSignatoryItems = (form: CustomDocumentForm): DropdownMenuItems =>
  signatureSignatoryOptions
    .filter((x) => {
      switch (x) {
        case "freetext":
          return true;
        default:
          return !form.signatories.some((y) => y.option === x);
      }
    })
    .map((x) => ({
      type: "item",
      id: x,
      label: signatoryOptionLabel[x],
      onSelect: () => form.addSignatory("signatories", x),
    }));

const quickAddSubscribers = (subscribers?: Subscriber[]): Recipient[] => {
  return subscribers?.map((s) => subscriberToRecipient(s)) ?? [];
};

const quickAddShareholders = (
  shareholders?: ShareholderDTO[],
  sharesForShareholders?: SharesForShareholder[],
): Recipient[] => {
  return shareholdersWithShares(shareholders ?? [], sharesForShareholders ?? []).map(
    shareholderToRecipient,
  );
};

const quickAddOptionHolders = (shareholders?: ShareholderDTO[]): Recipient[] =>
  (shareholders ?? []).filter((s) => s.isOptionHolder).map(shareholderToRecipient);

const quickAddBoardMembers = (roles?: CompanyRole[]): Recipient[] => {
  return sortedRoles(roles ?? [])
    .filter(isBoardMember)
    .map(companyRoleToRecipient);
};

const quickAddCeo = (roles?: CompanyRole[]): Recipient | undefined => {
  return when(
    roles?.find((r) => r.role === RoleType.CEO),
    companyRoleToRecipient,
  );
};

const quickAddChairperson = (roles?: CompanyRole[]): Recipient | undefined => {
  return when(
    roles?.find((r) => r.role === RoleType.CHAIRPERSON),
    companyRoleToRecipient,
  );
};

const toQuickAddItem = ({
  name,
  label,
  recipients,
  tooltip,
  isDynamic,
  isDisabled,
}: {
  name: string;
  label: TMessage;
  recipients: Recipient[];
  tooltip?: TMessage;
  isDynamic?: boolean;
  isDisabled?: boolean;
}): QuickAddItem => ({
  name,
  label,
  count: recipients.length,
  disabled: !recipients.length || isDisabled === true,
  recipients: recipients,
  tooltip,
  isDynamic,
});

export const quickAdd = (form: CustomDocumentForm, orgNo: string): QuickAddItem[] => {
  const context = form.context.contextData[orgNo];
  const optionHolders = quickAddOptionHolders(context?.initialState.shareholders);
  const debtConverters = form.filterDebtConverters(orgNo);
  const debtConvertersDisabled = form
    .getDynamicRecipients(orgNo)
    .some((recipient) => recipient.type === "debtConverters");
  return compact<QuickAddItem>([
    form.context?.subscribers &&
      toQuickAddItem({
        name: "subscribers",
        label: { key: "subscribers.title" },
        recipients: quickAddSubscribers(
          form.context.orgNo === orgNo ? form.context.subscribers : undefined,
        ),
      }),
    debtConverters &&
      toQuickAddItem({
        name: "debtConverters",
        label: { key: "debtConverters.button" },
        recipients: debtConverters,
        tooltip: { key: "debtConverters.tooltip" },
        isDynamic: true,
        isDisabled: debtConvertersDisabled,
      }),
    toQuickAddItem({
      name: "shareholders",
      label: { key: "actors.type.shareholder" },
      recipients: quickAddShareholders(
        context?.initialState.shareholders,
        context?.initialState.sharesForShareholders,
      ),
    }),
    optionHolders.length > 0 &&
      toQuickAddItem({
        name: "option-holders",
        label: { key: "actors.type.option-holders" },
        recipients: optionHolders,
      }),
    toQuickAddItem({
      name: "board-members",
      label: { key: "actors.type.board-members" },
      recipients: quickAddBoardMembers(context?.initialState.roles),
    }),
    when(quickAddCeo(context?.initialState.roles), (ceo) =>
      toQuickAddItem({
        name: "ceo",
        label: { key: "ceo-with-name", params: { name: ceo.name } },
        recipients: [ceo],
      }),
    ),
    when(quickAddChairperson(context?.initialState.roles), (chairperson) =>
      toQuickAddItem({
        name: "chairperson",
        label: { key: "chairperson-with-name", params: { name: chairperson.name } },
        recipients: [chairperson],
      }),
    ),
  ]).map((item) => withoutAdded(item, form.getRecipientsEditModel(orgNo)));
};

const withoutAdded = (item: QuickAddItem, added?: RecipientsEditModel): QuickAddItem => {
  if (item.isDynamic || !added) return item;
  const recipients = item.recipients.filter((r) => !added.hasRecipient(r));
  return {
    ...item,
    recipients,
    count: recipients.length,
    disabled: item.disabled || recipients.length === 0,
  };
};

export type BatchQuickAddItem = {
  name: string;
  label: TMessage;
  recipientsByCompany: { orgNo: string; recipient: Recipient }[];
  peopleToAdd: { name: string; companyCount: number }[];
  companiesWithoutRole: string[];
  companyCount: number;
};

const MAX_TOOLTIP_ITEMS = 5;

export const batchQuickAddTooltip = (
  {
    label,
    recipientsByCompany,
    peopleToAdd,
    companiesWithoutRole,
    companyCount,
  }: BatchQuickAddItem,
  requireSignature: boolean,
  t: TFunction,
): string => {
  const role = t(label).toLowerCase();

  const createBulletList = (heading: string, items: string[]) => {
    const shown = items.slice(0, MAX_TOOLTIP_ITEMS);
    const rest = items.length - shown.length;
    const listed =
      rest > 0
        ? [...shown, t("dbot.custom-document.recipients.batch-add.and-more", { count: rest })]
        : shown;
    return [heading, ...listed.map((item) => `- ${item}`)].join("\n");
  };

  const addsRolePerCompany = t(
    requireSignature
      ? "dbot.custom-document.recipients.batch-add.intro.sign"
      : "dbot.custom-document.recipients.batch-add.intro.receive",
    { role },
  );

  const peopleToBeAdded = peopleToAdd.length
    ? createBulletList(
        t("dbot.custom-document.recipients.batch-add.will-add", {
          count: recipientsByCompany.length,
        }),
        peopleToAdd.map((person) =>
          t("dbot.custom-document.recipients.batch-add.person", {
            name: person.name,
            count: person.companyCount,
          }),
        ),
      )
    : undefined;

  const companiesWithoutTheRole = companiesWithoutRole.length
    ? createBulletList(
        t("dbot.custom-document.recipients.batch-add.missing", {
          count: companiesWithoutRole.length,
          total: companyCount,
          role,
        }),
        companiesWithoutRole,
      )
    : undefined;

  const addSeparatelyAdvice = companiesWithoutRole.length
    ? t(
        requireSignature
          ? "dbot.custom-document.recipients.batch-add.separately.sign"
          : "dbot.custom-document.recipients.batch-add.separately.receive",
      )
    : undefined;

  return compact([
    addsRolePerCompany,
    peopleToBeAdded,
    companiesWithoutTheRole,
    addSeparatelyAdvice,
  ]).join("\n\n");
};

export const batchQuickAdd = (form: CustomDocumentForm): BatchQuickAddItem[] => {
  const alreadyHasRecipient = (orgNo: string, recipient: Recipient) =>
    form.getRecipientsEditModel(orgNo)?.recipientFields.some((r) => r.ref === recipient.ref) ===
    true;
  const recipientsToAdd = (pickRole: (roles?: CompanyRole[]) => Recipient | undefined) =>
    compact(
      form.context.companies.map(({ orgNo }) =>
        when(pickRole(form.context.contextData[orgNo]?.initialState.roles), (recipient) =>
          alreadyHasRecipient(orgNo, recipient) ? undefined : { orgNo, recipient },
        ),
      ),
    );
  const companiesWithoutRole = (pickRole: (roles?: CompanyRole[]) => Recipient | undefined) =>
    form.context.companies
      .filter(({ orgNo }) => !pickRole(form.context.contextData[orgNo]?.initialState.roles))
      .map(({ name }) => name)
      .toSorted((a, b) => a.localeCompare(b));

  const toBatchQuickAddItem = (
    name: string,
    label: TMessage,
    pickRole: (roles?: CompanyRole[]) => Recipient | undefined,
  ): BatchQuickAddItem => {
    const recipientsByCompany = recipientsToAdd(pickRole);
    return {
      name,
      label,
      recipientsByCompany,
      peopleToAdd: groupByPerson(recipientsByCompany),
      companiesWithoutRole: companiesWithoutRole(pickRole),
      companyCount: form.context.companies.length,
    };
  };

  return [
    toBatchQuickAddItem("ceo", getRoleTypeLabel(RoleType.CEO), quickAddCeo),
    toBatchQuickAddItem("chairperson", getRoleTypeLabel(RoleType.CHAIRPERSON), quickAddChairperson),
  ];
};

const groupByPerson = (recipientsByCompany: { recipient: Recipient }[]) =>
  Object.entries(countBy(recipientsByCompany, ({ recipient }) => recipient.name))
    .map(([name, companyCount]) => ({ name, companyCount }))
    .toSorted((a, b) => b.companyCount - a.companyCount);

export const removeAllRecipients = (form: CustomDocumentForm) => {
  form.context.companies.forEach(({ orgNo }) => {
    const model = form.getRecipientsEditModel(orgNo);
    model?.recipientFields.slice().forEach((r) => model.removeRecipient(r.id));
    model
      ?.toDynamicRecipients()
      ?.slice()
      .forEach((r) => model.removeDynamicRecipient(r.type));
  });
  form.validate();
};

export const hasAnyRecipient = (form: CustomDocumentForm) =>
  form.context.companies.some(({ orgNo }) => form.recipientCount(orgNo) > 0);

export function openCustomDocumentRecipientsPanelEvent(documentId: string): DbotEvent {
  return {
    type: "dbot/open-details-panel",
    panel: { type: "custom", id: "recipients", documentId },
  };
}
